import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  CheckCircle2, Clock, HelpCircle, ChevronDown,
  Phone, Mail, Award, Send
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/EnquiryForm.scss';

export default function AdmissionEnquiry() {
  const location = useLocation();
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    studentName: '',
    fatherName: '',
    grade: '',
    currentSchool: '',
    address: '',
    contactNo: '',
    enquiry: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://vhserver.desync.in';
      const response = await fetch(`${apiUrl}/public/api/school/ptvn_goraul/admission-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Admission enquiry submitted successfully! Our admissions counsellor will contact you within 24 hours.' });
        setFormData({
          studentName: '',
          fatherName: '',
          grade: '',
          currentSchool: '',
          address: '',
          contactNo: '',
          enquiry: ''
        });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to submit enquiry. Please try again or call our office directly.' });
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setMessage({ type: 'success', text: 'Thank you! Your admission enquiry has been recorded. Our admissions coordinator will contact you shortly at ' + (formData.contactNo || 'your provided number') + '.' });
      setFormData({
        studentName: '',
        fatherName: '',
        grade: '',
        currentSchool: '',
        address: '',
        contactNo: '',
        enquiry: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const { t } = useTranslation();

  const navItems = [
    { id: 'admission-process', label: t('nav_admission_process') },
    { id: 'required-documents', label: t('nav_required_documents') },
    { id: 'age-criteria', label: t('nav_age_criteria') },
    { id: 'admission-guidelines', label: t('nav_admission_guidelines') },
    { id: 'enquiry-info', label: t('nav_online_enquiry') },
    { id: 'enquiry-form', label: t('nav_enquiry_form') },
    { id: 'admission-faqs', label: t('nav_admission_faqs') },
  ];

  const steps = [
    { num: '1', title: 'Submit Enquiry', desc: 'Fill the online enquiry form or visit the school reception to collect the registration form.' },
    { num: '2', title: 'Registration', desc: 'Submit the completed form along with candidate photographs and baseline documents.' },
    { num: '3', title: 'Student Interaction', desc: 'Informal friendly interaction for pre-primary; basic readiness assessment for Class 1 to 10.' },
    { num: '4', title: 'Document Verification', desc: 'Verify original birth certificate, previous school Transfer Certificate (TC), and mark sheets.' },
    { num: '5', title: 'Fee Payment & Enrollment', desc: 'Pay the admission fee to confirm the seat, receive the student ID kit, and welcome book.' }
  ];

  const documents = [
    { title: 'Birth Certificate', note: 'Original and photocopy issued by Municipal Corporation or Gram Panchayat.' },
    { title: 'Transfer Certificate (TC)', note: 'Original TC from previous recognized school (applicable for Class 2 onwards).' },
    { title: 'Previous Class Report Card', note: 'Photocopy of previous class pass mark sheet / evaluation report.' },
    { title: 'Passport-size Photographs', note: '4 recent colored passport photos of the student, plus 2 of parents/guardian.' },
    { title: 'Aadhaar Card Copies', note: 'Photocopy of student Aadhaar card and parents’ Aadhaar for identity verification.' },
    { title: 'Address & Category Proof', note: 'Residential address proof (Ration card/Electricity bill) and Caste certificate if applicable.' }
  ];

  const ageCriteria = [
    { class: 'Playgroup / Pre-Nursery', age: '2.5 to 3 Years', cutoff: 'As on 31st March of academic year' },
    { class: 'Nursery', age: '3 to 4 Years', cutoff: 'As on 31st March of academic year' },
    { class: 'L.K.G. (Lower Kindergarten)', age: '4 to 5 Years', cutoff: 'As on 31st March of academic year' },
    { class: 'U.K.G. (Upper Kindergarten)', age: '5 to 6 Years', cutoff: 'As on 31st March of academic year' },
    { class: 'Class 1', age: '6 to 7 Years', cutoff: 'As on 31st March of academic year' },
    { class: 'Class 2 to Class 10', age: 'Corresponding Age Bracket', cutoff: 'Subject to valid pass TC & mark sheet from previous recognized school' },
  ];

  const guidelines = [
    'Admissions are granted strictly on a first-come, first-served basis subject to seat availability in each section.',
    'For Pre-Primary admissions (Play to UKG), there is no formal entrance examination. Admissions are based on candidate-parent informal interaction.',
    'For admission into Classes 1 to 9, students undergo a basic conceptual diagnostic assessment in English, Mathematics, and Hindi.',
    'A valid Transfer Certificate (TC) counter-signed by the competent educational authority is mandatory for students transferring from other schools.',
    'Special fee consideration and sibling concessions are available for second and third biological children enrolled concurrently.',
    'Fee once paid towards registration and admission processing is non-refundable as per institutional norms.'
  ];

  const admissionFaqs = [
    {
      q: 'What is the admission procedure for the new academic session?',
      a: 'Parents can start by submitting the online enquiry form or visiting our school office at Rajkhand, Goraul. After registration, a brief student readiness assessment is scheduled, followed by document verification and fee payment to secure admission.'
    },
    {
      q: 'Is there any written entrance test for Nursery or KG admissions?',
      a: 'No. Admissions for Nursery, LKG, and UKG are conducted without any formal written examination. We conduct a friendly, welcoming interaction to observe developmental readiness and social comfort.'
    },
    {
      q: 'Can a student take mid-session admission if transferring from another city?',
      a: 'Yes, mid-session admissions are accepted subject to vacant seats in the requested class and submission of an original Transfer Certificate (TC) along with recent report cards.'
    },
    {
      q: 'Does the school provide transport facilities for new students?',
      a: 'Yes, our fleet of school buses and vans covers multiple routes across Rajkhand, Goraul, and surrounding areas within a 10 km radius. Transport seats can be requested along with admission.'
    },
    {
      q: 'What are the school fee payment modes?',
      a: 'School fees are collected in cash directly at the school accounts counter during office hours (Monday to Saturday, 8:00 AM – 3:00 PM). An official printed receipt is provided upon payment. Online payments or UPI transfers are not accepted.'
    }
  ];

  return (
    <div className="admission-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>Admission - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Complete admission information for Prabhat Tara Vidya Niketan Goraul: Process, Required documents, Age criteria, Guidelines, Online enquiry form, and FAQs."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/admission-enquiry" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <div className="hero-inner">
          <span className="badge">
            <Award size={16} /> Admissions Open | Nursery to Class 10
          </span>
          <h1>Admissions at Prabhat Tara Vidya Niketan</h1>
          <p>
            Join a 30-year legacy of academic excellence, holistic personal growth, modern computer coding, and values-driven mentorship.
          </p>
        </div>
      </div>

      {/* Nav Pills Bar */}
      <div className="nav-pills-bar">
        <div className="nav-container">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div className="admission-content-wrapper">

        {/* 1. ADMISSION PROCESS */}
        <section id="admission-process" className="admission-card">
          <span className="card-tag">1. Step-by-Step Guide</span>
          <h2>Admission Process</h2>
          <p className="card-desc">
            We have designed an open, transparent, and seamless five-stage enrollment procedure for parents and aspiring students.
          </p>

          <div className="process-steps">
            {steps.map((s, idx) => (
              <div key={idx} className="step-box">
                <div className="step-num">{s.num}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 2. REQUIRED DOCUMENTS */}
        <section id="required-documents" className="admission-card">
          <span className="card-tag">2. Verification Checklist</span>
          <h2>Required Documents for Admission</h2>
          <p className="card-desc">
            Please ensure you have both the original copies (for on-the-spot verification) and self-attested photocopies of the following credentials when completing enrollment.
          </p>

          <div className="docs-grid">
            {documents.map((doc, idx) => (
              <div key={idx} className="doc-item">
                <CheckCircle2 size={22} className="check-icon" />
                <div>
                  <strong>{doc.title}</strong>
                  <span>{doc.note}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 3. AGE CRITERIA FOR ADMISSION */}
        <section id="age-criteria" className="admission-card">
          <span className="card-tag">3. Age Eligibility</span>
          <h2>Age Criteria for Admission</h2>
          <p className="card-desc">
            Age parameters strictly adhere to national education regulations. The child’s age is computed as of <strong>31st March</strong> of the respective academic year.
          </p>

          <div className="age-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Class / Grade</th>
                  <th>Prescribed Age Bracket</th>
                  <th>Cut-off Reference</th>
                </tr>
              </thead>
              <tbody>
                {ageCriteria.map((ac, idx) => (
                  <tr key={idx}>
                    <td className="class-name">{ac.class}</td>
                    <td className="age-range">{ac.age}</td>
                    <td className="cutoff-info">{ac.cutoff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. ADMISSION GUIDELINES */}
        <section id="admission-guidelines" className="admission-card">
          <span className="card-tag">4. Rules & Policies</span>
          <h2>Admission Guidelines & Policies</h2>
          <p className="card-desc">
            Important terms and conditions governing student enrollment, assessments, seat confirmations, and concessions.
          </p>

          <ul className="guidelines-list">
            {guidelines.map((g, idx) => (
              <li key={idx}>
                <CheckCircle2 size={20} className="check-icon" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* 5. ONLINE ADMISSION ENQUIRY INFO */}
        <section id="enquiry-info" className="admission-card assistance-desk-card">
          <span className="card-tag">5. Online Admission Enquiry</span>
          <h2>Instant Admission Assistance Desk</h2>
          <p className="card-desc">
            Have a question before submitting your form? Reach out directly to our admissions counselors via phone, email, or WhatsApp.
          </p>

          <div className="assistance-channels">
            <div className="channel-box">
              <Phone size={26} className="channel-icon" />
              <h4>Call Admissions Team</h4>
              <p><a href="tel:7654164726">+91 76541 64726</a></p>
              <p><a href="tel:9939988606">+91 99399 88606</a></p>
              <span className="sub-text">Dedicated helpline for queries</span>
            </div>

            <div className="channel-box">
              <Mail size={26} className="channel-icon" />
              <h4>Email Queries</h4>
              <p><a href="mailto:principal@prabhatvidyanikethan.com">principal@prabhatvidyanikethan.com</a></p>
              <span className="sub-text">Response guaranteed within 24 hours</span>
            </div>

            <div className="channel-box">
              <Clock size={26} className="channel-icon" />
              <h4>Counseling Hours</h4>
              <p>Mon - Sat: 08:30 AM – 03:00 PM</p>
              <span className="sub-text">Campus visits & guided tours available</span>
            </div>
          </div>
        </section>

        {/* 6. ADMISSION ENQUIRY FORM */}
        <section id="enquiry-form" className="enquiry-form-section">
          <div className="form-banner">
            <span className="form-tag">6. Online Registration</span>
            <h2>Admission Enquiry Form</h2>
            <p>Academic Session 2026-2027 | Nursery to Class 10</p>
          </div>

          {message.text && (
            <div className={`message-banner ${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="enquiry-form-body">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="studentName">Student Full Name *</label>
                <input
                  type="text"
                  id="studentName"
                  name="studentName"
                  value={formData.studentName}
                  onChange={handleChange}
                  required
                  placeholder="Enter student's full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="fatherName">Father's / Guardian's Name *</label>
                <input
                  type="text"
                  id="fatherName"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  required
                  placeholder="Enter father's / guardian's name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="grade">Class Seeking Admission *</label>
                <select
                  id="grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Class</option>
                  <option value="Play Class">Play Class</option>
                  <option value="Nursery">Nursery</option>
                  <option value="LKG">LKG</option>
                  <option value="UKG">UKG</option>
                  <option value="Class 1">Class 1</option>
                  <option value="Class 2">Class 2</option>
                  <option value="Class 3">Class 3</option>
                  <option value="Class 4">Class 4</option>
                  <option value="Class 5">Class 5</option>
                  <option value="Class 6">Class 6</option>
                  <option value="Class 7">Class 7</option>
                  <option value="Class 8">Class 8</option>
                  <option value="Class 9">Class 9</option>
                  <option value="Class 10">Class 10</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="contactNo">Contact Mobile Number *</label>
                <input
                  type="tel"
                  id="contactNo"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="currentSchool">Previous School (if applicable)</label>
              <input
                type="text"
                id="currentSchool"
                name="currentSchool"
                value={formData.currentSchool}
                onChange={handleChange}
                placeholder="Current / Previous school attended"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Residential Address *</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                rows="3"
                placeholder="Village / Town, Landmark, Block, PIN Code"
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="enquiry">Specific Questions / Special Requirements</label>
              <textarea
                id="enquiry"
                name="enquiry"
                value={formData.enquiry}
                onChange={handleChange}
                rows="3"
                placeholder="Ask about school transport, special coaching, sibling discount, etc."
              ></textarea>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              <Send size={18} /> {loading ? 'Submitting Enquiry...' : 'Submit Admission Enquiry'}
            </button>
          </form>

          <div className="form-contact-footer">
            <h3>Need Immediate Help?</h3>
            <p><strong>Phone:</strong> +91 7654164726, +91 9939988606</p>
            <p><strong>Campus Address:</strong> Rajkhand, Goraul, Vaishali, Bihar - 844118</p>
            <p><strong>Office Timings:</strong> Monday to Saturday: 8:00 AM - 3:30 PM</p>
          </div>
        </section>

        {/* 7. FREQUENTLY ASKED QUESTIONS */}
        <section id="admission-faqs" className="admission-card">
          <span className="card-tag">7. Clarifications</span>
          <h2>Admission FAQs</h2>
          <p className="card-desc">
            Quick answers to frequently asked questions regarding the admission cycle, documents, and school facilities.
          </p>

          <div className="faqs-container">
            {admissionFaqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-card ${activeFaq === index ? 'active' : ''}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="faq-trigger"
                  aria-expanded={activeFaq === index}
                >
                  <span className="faq-title">
                    <HelpCircle size={18} className="faq-icon" /> {faq.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`faq-chevron ${activeFaq === index ? 'rotated' : ''}`}
                  />
                </button>
                {activeFaq === index && (
                  <div className="faq-body">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
